import { INTERVIEW_QUALITY_LABELS, type InterviewQualityFilter } from '@/lib/interview-quality';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type { TableActionItem } from '@/components/shared';
import { isUuid } from '@/lib/uuid';
import { usePersistedSearchParams } from '@/preferences';
import type {
  BackofficeProjectInterviewRow,
  BackofficeProjectInterviewsResponse,
  InterviewSortBy,
  SortDirection,
} from '../types';
import { ProjectInterviewsFilters } from './ProjectInterviewsFilters';
import { ProjectInterviewsTable } from './ProjectInterviewsTable';

const DEFAULT_LIMIT = 20;
const QUALITY_STATUS_VALUES = new Set(Object.keys(INTERVIEW_QUALITY_LABELS));
const SORTABLE_FIELDS = new Set<InterviewSortBy>(['study', 'duration', 'createdAt']);

export interface ProjectInterviewsListSectionProps {
  contextLabel: string;
  showStudyFilter?: boolean;
  showStudyOwner?: boolean;
  preferenceScope?: string;
  surface?: 'page' | 'tile';
  loadPage: (
    searchParams: URLSearchParams,
    signal: AbortSignal
  ) => Promise<Pick<BackofficeProjectInterviewsResponse, 'interviews' | 'page'>>;
}

function readQualityStatuses(value: string | null): InterviewQualityFilter[] {
  return [
    ...new Set(
      (value ?? '')
        .split(',')
        .filter((status): status is InterviewQualityFilter => QUALITY_STATUS_VALUES.has(status))
    ),
  ];
}

function readSortBy(value: string | null, allowStudySort: boolean): InterviewSortBy {
  if (!value || !SORTABLE_FIELDS.has(value as InterviewSortBy)) {
    return 'createdAt';
  }
  if (value === 'study' && !allowStudySort) {
    return 'createdAt';
  }
  return value as InterviewSortBy;
}

function readSortDirection(value: string | null): SortDirection {
  return value === 'asc' ? 'asc' : 'desc';
}

export function ProjectInterviewsListSection({
  contextLabel,
  showStudyFilter = false,
  showStudyOwner = false,
  preferenceScope = showStudyFilter ? 'list:interviews' : 'list:project-interviews',
  surface = 'page',
  loadPage,
}: ProjectInterviewsListSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const persistedSearchParamKeys = useMemo(
    () =>
      [
        'qualityStatus',
        showStudyFilter ? 'studyId' : null,
        'startedFrom',
        'startedTo',
        'sortBy',
        'sortDirection',
      ].filter((key): key is string => Boolean(key)),
    [showStudyFilter]
  );
  const [interviews, setInterviews] = useState<BackofficeProjectInterviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const loadingMoreRef = useRef(false);
  const refreshAbortControllerRef = useRef<AbortController | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const qualityParam = searchParams.get('qualityStatus');
  const qualityStatuses = useMemo(() => readQualityStatuses(qualityParam), [qualityParam]);
  const studyId = showStudyFilter ? (searchParams.get('studyId') ?? '') : '';
  const requestStudyId = isUuid(studyId) ? studyId : '';
  const startedFrom = searchParams.get('startedFrom') ?? '';
  const startedTo = searchParams.get('startedTo') ?? '';
  const sortBy = readSortBy(searchParams.get('sortBy'), showStudyFilter);
  const sortDirection = readSortDirection(searchParams.get('sortDirection'));

  const requestParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection);
    if (qualityStatuses.length) params.set('qualityStatus', qualityStatuses.join(','));
    if (requestStudyId) params.set('studyId', requestStudyId);
    if (startedFrom) params.set('startedFrom', startedFrom);
    if (startedTo) params.set('startedTo', startedTo);
    return params;
  }, [qualityStatuses, requestStudyId, sortBy, sortDirection, startedFrom, startedTo]);

  const hasLiveCall = interviews.some(
    (interview) =>
      interview.outboundCall &&
      (interview.outboundCall.status !== 'ended' ||
        interview.outboundCall.resultsStatus === 'pending')
  );

  // Preserve the loaded page range while refreshing live calls. Transient errors keep the last good rows.
  useEffect(() => {
    if (!hasLiveCall || initialLoading) return;
    let controller: AbortController | undefined;
    const timer = window.setInterval(() => {
      if (
        document.visibilityState === 'hidden' ||
        loadingMoreRef.current ||
        refreshAbortControllerRef.current ||
        controller
      )
        return;
      const pending = new AbortController();
      controller = pending;
      loadingMoreRef.current = true;
      void (async () => {
        const rows: BackofficeProjectInterviewRow[] = [];
        let count = total;
        for (let offset = 0; offset < interviews.length; offset += 500) {
          const params = new URLSearchParams(requestParams);
          params.set('limit', String(Math.min(500, interviews.length - offset)));
          params.set('offset', String(offset));
          const response = await loadPage(params, pending.signal);
          rows.push(...response.interviews);
          count = response.page.total;
        }
        if (!pending.signal.aborted) {
          setInterviews(rows);
          setTotal(count);
        }
      })()
        .catch(() => {
          /* Retry on the next interval; preserve visible evidence. */
        })
        .finally(() => {
          if (controller === pending) {
            controller = undefined;
            loadingMoreRef.current = false;
          }
        });
    }, 5000);
    return () => {
      window.clearInterval(timer);
      controller?.abort();
      if (controller) loadingMoreRef.current = false;
    };
  }, [hasLiveCall, initialLoading, interviews.length, loadPage, requestParams, total]);

  const hasMore = interviews.length < total;

  const loadInterviews = useCallback(
    async (offset: number, signal: AbortSignal) => {
      const params = new URLSearchParams(requestParams);
      params.set('limit', String(DEFAULT_LIMIT));
      params.set('offset', String(offset));

      const response = await loadPage(params, signal);
      setTotal(response.page.total);
      setInterviews((current) =>
        offset === 0 ? response.interviews : [...current, ...response.interviews]
      );
    },
    [loadPage, requestParams]
  );

  const handleRefresh = useCallback(async () => {
    refreshAbortControllerRef.current?.abort();
    const controller = new AbortController();
    refreshAbortControllerRef.current = controller;

    setRefreshing(true);
    setError(null);
    setLoadMoreError(null);

    try {
      await loadInterviews(0, controller.signal);
    } catch (caught: unknown) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        return;
      }
      setInterviews([]);
      setTotal(0);
      setError(caught instanceof Error ? caught.message : 'Failed to refresh interviews');
    } finally {
      if (refreshAbortControllerRef.current === controller) {
        refreshAbortControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setRefreshing(false);
      }
    }
  }, [loadInterviews]);

  const tableActions = useMemo<TableActionItem[]>(
    () => [
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        onSelect: () => {
          void handleRefresh();
        },
        placement: 'fixed-inline',
        disabled: initialLoading,
        isLoading: refreshing,
      },
    ],
    [handleRefresh, initialLoading, refreshing]
  );

  useEffect(() => {
    const controller = new AbortController();
    setInitialLoading(true);
    setLoadingMore(false);
    setError(null);
    setLoadMoreError(null);

    loadInterviews(0, controller.signal)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setInterviews([]);
        setTotal(0);
        setError(caught instanceof Error ? caught.message : 'Failed to load interviews');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setInitialLoading(false);
        }
      });

    return () => controller.abort();
  }, [loadInterviews]);

  useEffect(() => {
    return () => refreshAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || initialLoading || !hasMore) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const controller = new AbortController();
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || loadingMoreRef.current) {
          return;
        }

        observer.disconnect();
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setLoadMoreError(null);
        loadInterviews(interviews.length, controller.signal)
          .catch((caught: unknown) => {
            if (caught instanceof DOMException && caught.name === 'AbortError') {
              return;
            }
            setLoadMoreError(
              caught instanceof Error ? caught.message : 'Failed to load more interviews'
            );
          })
          .finally(() => {
            loadingMoreRef.current = false;
            if (!controller.signal.aborted) {
              setLoadingMore(false);
            }
          });
      },
      { rootMargin: '280px' }
    );

    observer.observe(target);

    return () => {
      controller.abort();
      observer.disconnect();
    };
  }, [hasMore, initialLoading, interviews.length, loadInterviews]);

  usePersistedSearchParams({
    scope: preferenceScope,
    keys: persistedSearchParamKeys,
    searchParams,
    setSearchParams,
  });

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  }

  function handleSortChange(nextSortBy: InterviewSortBy) {
    const nextDirection = sortBy === nextSortBy && sortDirection === 'asc' ? 'desc' : 'asc';
    updateParams({ sortBy: nextSortBy, sortDirection: nextDirection });
  }

  return (
    <section
      className={
        surface === 'tile'
          ? 'flex h-full min-h-full flex-col overflow-hidden bg-white'
          : 'overflow-hidden rounded-xl border border-neutral-200 bg-white'
      }
    >
      <ProjectInterviewsFilters
        qualityStatuses={qualityStatuses}
        studyId={studyId}
        startedFrom={startedFrom}
        startedTo={startedTo}
        actions={tableActions}
        showStudyFilter={showStudyFilter}
        onQualityStatusesChange={(statuses) => updateParams({ qualityStatus: statuses.join(',') })}
        onStudyIdChange={(value) => updateParams({ studyId: value })}
        onStartedFromChange={(value) => updateParams({ startedFrom: value })}
        onStartedToChange={(value) => updateParams({ startedTo: value })}
      />

      {initialLoading ? (
        <div className="px-5 py-16 text-center text-body text-neutral-500">Loading interviews</div>
      ) : error ? (
        <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
      ) : interviews.length > 0 ? (
        <div className={surface === 'tile' ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
          <ProjectInterviewsTable
            interviews={interviews}
            projectTitle={contextLabel}
            showStudyMobileContext={showStudyFilter}
            showStudySort={showStudyFilter}
            showStudyOwner={showStudyOwner}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
          <div ref={loadMoreRef} className="border-t border-neutral-200 px-5 py-4 text-center">
            {loadingMore ? (
              <span className="text-small text-neutral-500">Loading more interviews</span>
            ) : loadMoreError ? (
              <span className="text-small text-red-700">{loadMoreError}</span>
            ) : hasMore ? (
              <span className="text-small text-neutral-500">Scroll to load more</span>
            ) : (
              <span className="text-small text-neutral-500">
                Showing {interviews.length} of {total}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="px-5 py-16 text-center text-body text-neutral-500">No interviews found</div>
      )}
    </section>
  );
}
